import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, { checkTokenValidity, CheckUserExpiry, getUserDetails,
  handleLegacySessionExpired
} from "../../components/content/axiosConfig";
import Swal from 'sweetalert2';
import CustomModal from "../../components/custom/customModal";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";


const PondNote = () => {
	const [notes, setNotes] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [ponds, setPonds] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [modalIsOpen, setModalIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [entriesPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState('');
	const fisherID = getUserDetails().FisherID;
	const staffID = getUserDetails().id;

	const [modalData, setModalData] = useState({
		Note: "",
		PondDID: 0,
		FisherID: fisherID,
		StaffID: staffID
	});

	const [isEditing, setIsEditing] = useState(false);
	const [editId, setEditId] = useState(null);

	const FetchPondNote = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await axiosInstance.get(`/api/PondNote/GetAllNotes/${fisherID}`, {
				params: { search: searchTerm, page: currentPage, limit: entriesPerPage }
			});
			const body = response.data;
			if (Array.isArray(body)) {
				setNotes(body);
				setTotalCount(body.length);
			} else {
				setNotes(body.data || []);
				setTotalCount(Number(body.total) || 0);
			}
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
				const errorMessage = error.response?.data?.message || "Failed to fetch pond notes";
				showErrorToast(errorMessage);
			}
			console.error("Error fetching pond notes:", error);
		}
	}, [fisherID, searchTerm, currentPage, entriesPerPage]);

	const FetchPond = useCallback(async () => {
		try {
			setIsLoading(true);
			const response = await axiosInstance.get(`api/PondDetail/GetAllDetails/${fisherID}`, {
				params: { search: null }
			});
			const users = response.data;
			setPonds(users);
			setIsLoading(false);
		} catch (error) {
			setIsLoading(false);
			if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
				const errorMessage = error.response?.data?.message || "Failed to fetch ponds";
				showErrorToast(errorMessage);
			}
			console.error("Error fetching ponds:", error);
		}
	}, [fisherID]);

	const CheckExpiry = useCallback(async () => {
		await CheckUserExpiry();
	}, []);

	useEffect(() => {
		checkTokenValidity();
		CheckExpiry();
		FetchPond();
	}, [FetchPond, CheckExpiry]);

	useEffect(() => {
		const delay = searchTerm ? 800 : 0;
		const id = setTimeout(() => FetchPondNote(), delay);
		return () => clearTimeout(id);
	}, [FetchPondNote, searchTerm, currentPage]);

	const indexOfFirstEntry = totalCount === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
	const indexOfLastEntry = Math.min(currentPage * entriesPerPage, totalCount);
	const currentEntries = notes;
	const totalPages = Math.ceil(totalCount / entriesPerPage) || 1;

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
	};

	const DeleteUser = async (id) => {
		const result = await Swal.fire({
			title: 'Are you sure?',
			text: "You won't be able to revert this!",
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#3085d6',
			cancelButtonColor: '#d33',
			confirmButtonText: 'Yes, delete it!',
			cancelButtonText: 'Cancel',
		});

		if (result.isConfirmed) {
			setIsLoading(true);
			try {
				await axiosInstance.delete(`/api/PondNote/DeleteNote/${id}`, {
					params: { FisherID: fisherID }
				});
				showSuccessToast("Pond note deleted successfully");
				await FetchPondNote();
				setIsLoading(false);
			} catch (error) {
				setIsLoading(false);
				if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
					const errorMessage = error.response?.data?.message || "Failed to delete pond note";
					showErrorToast(errorMessage);
				}
				console.error("Error deleting pond note:", error);
			}
		}
	};

	const openModal = (data = null) => {
		if (data) {
			console.log(data);
			setIsEditing(true);
			setEditId(data.PondNoteID);
			setModalData({
				Note: data.PondNote,
				PondDID: data.PondDID,
				FisherID: fisherID,
				StaffID: staffID
			});
		} else {
			setIsEditing(false);
			setEditId(null);
			setModalData({
				Note: "",
				PondDID: 0,
				FisherID: fisherID,
				StaffID: staffID
			});
		}
		setModalIsOpen(true);
	};

	const closeModal = () => {
		setModalIsOpen(false);
	};

	const handleChange = (e) => {
		const { id, name, value, type, checked } = e.target;

		if (type === "radio") {
			setModalData({
				...modalData,
				[name]: value === "true",
			});
		} else {
			setModalData({
				...modalData,
				[id]: type === "checkbox" ? checked : value,
			});
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoading(true);
		try {
			// Validation for required fields
			if (!modalData.PondDID || modalData.PondDID === 0) {
				showErrorToast("Please select a pond detail");
				setIsLoading(false);
				return;
			}
			if (!modalData.Note.trim()) {
				showErrorToast("Note cannot be empty");
				setIsLoading(false);
				return;
			}

			if (isEditing) {
				await axiosInstance.put(`/api/PondNote/UpdateNote/${editId}`, modalData);
				showSuccessToast("Pond note updated successfully");
			} else {
				await axiosInstance.post(`/api/PondNote/AddNote`, modalData);
				showSuccessToast("Pond note added successfully");
			}
			FetchPondNote();
			setIsLoading(false);
			closeModal();
		} catch (error) {
			setIsLoading(false);
			if (error.response && error.response.status === 401) {
        const auth = handleLegacySessionExpired(error);
        showErrorToast(auth.message);
      } else {
				const errorMessage = error.response?.data?.message || "Failed to save pond note";
				showErrorToast(errorMessage);
			}
			console.error("Error submitting form:", error);
		}
	};

	return (
		isLoading ? <Preloader /> :

			<div>
				<div className="d-flex align-items-center mb-3">
					<div>
						<ul className="breadcrumb">
							<li className="breadcrumb-item">
								<Link to="/">Home</Link>
							</li>
							<li className="breadcrumb-item Active">Add Pond Note </li>
						</ul>
						<h1 className="page-header mb-0"> Add Pond Note </h1>
					</div>
					<div className="ms-auto">
						<button
							type="button"
							className="btn btn-success btn-rounded px-4 rounded-pill"
							onClick={() => openModal()}
						>
							<i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
							Add Note
						</button>
					</div>
				</div>
				<div className="card border-0">
					<div className="tab-content p-3">
						<div className="tab-pane fade show active" id="allTab">
							<div className="input-group mb-3">
								<div className="flex-fill position-relative">
									<div className="input-group">
										<div
											className="input-group-text position-absolute top-0 bottom-0 bg-none border-0 start-0"
											style={{ zIndex: 10 }}
										>
											<i className="fa fa-search opacity-5"></i>
										</div>
										<input
											type="text"
											className="form-control px-35px bg-light"
											placeholder="Search Pond..."
											onChange={(e) => {
												setSearchTerm(e.target.value);
												setCurrentPage(1);
											}}
										/>
									</div>
								</div>
							</div>
							<div className="table-responsive mb-3">
								<table className="table table-hover table-panel text-nowrap align-middle mb-0">
									<thead>
										<tr>
											<th>Pond Name</th>
											<th>Note</th>
											<th>Update Date</th>
											<th>By Staff</th>
											<th>Actions </th>
										</tr>
									</thead>
									<tbody>
										{currentEntries.map((web) => (
											<tr key={web.PondNoteID}>
												<td>{web.PondName}</td>
												<td>{web.PondNote}</td>
												<td>{web.UpdatedAt}</td>
												<td>{web.StaffName}</td>
												<td>
													<div className="donezo-row-actions">
														<button
															type="button"
															className="btn btn-sm btn-primary"
															title="Edit note"
															aria-label="Edit note"
															onClick={() => openModal(web)}
														>
															<i className="fa fa-pencil" aria-hidden />
														</button>
														<button
															type="button"
															className="btn btn-sm btn-danger"
															title="Delete note"
															aria-label="Delete note"
															onClick={() => DeleteUser(web.PondNoteID)}
														>
															<i className="fa fa-trash" aria-hidden />
														</button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
							<div className="d-md-flex align-items-center">
								<div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
									Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalCount} entries
								</div>
								<ul className="pagination mb-0 justify-content-center">
									<li
										className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
									>
										<button
											className="page-link"
											onClick={() => handlePageChange(currentPage - 1)}
										>
											Previous
										</button>
									</li>
									{[...Array(totalPages)].map((_, index) => (
										<li
											key={index}
											className={`page-item ${index + 1 === currentPage ? "active" : ""
												}`}
										>
											<button
												className="page-link"
												onClick={() => handlePageChange(index + 1)}
											>
												{index + 1}
											</button>
										</li>
									))}
									<li
										className={`page-item ${currentPage === totalPages ? "disabled" : ""
											}`}
									>
										<button
											className="page-link"
											onClick={() => handlePageChange(currentPage + 1)}
										>
											Next
										</button>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>

				<CustomModal
					isOpen={modalIsOpen}
					onClose={closeModal}
					title={isEditing ? "Edit Pond Note" : "Add Pond Note"}
					showButton={true}
					modalSize="modal-lg"
					minHeight="400px"
					maxHeight="90vh"
					buttonText="Submit"
					onSubmit={handleSubmit}
				>

					<div className="card border-0">
						<div className="tab-content p-3">
							<div className="tab-pane fade show active" id="allTab">
								<div className="form-floating mb-25px">
									<select
										className="form-control h-50px fs-13px"
										id="PondDID"
										name="PondDID"
										value={modalData.PondDID}
										onChange={handleChange}
									>
										<option value="">Select Fishing Data</option>
										{ponds.map((web) => (
											<option key={web.id} value={web.id}>
												{web.Name}
											</option>
										))}
									</select>
									<label
										htmlFor="PondID"
										className="d-flex align-items-center fs-13px text-gray-600"
									>
										Pond Detail &nbsp; <span>*</span>
									</label>
								</div>

								<div className="mb-15px">
									<label
										htmlFor="Note"
										className="d-flex align-items-center fs-13px text-gray-600 mb-1"
									>
										Note &nbsp; <span>*</span>
									</label>
									<textarea
										className="form-control fs-13px"
										placeholder="Note"
										id="Note"
										name="Note"
										value={modalData.Note}
										onChange={handleChange}
										rows={5}
										style={{ resize: "vertical", minHeight: "7.5rem" }}
									/>
								</div>
							</div>
						</div>
					</div>

				</CustomModal>

			</div>
	);
};

export default PondNote;